import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BIOSDK_API_BASE_URL, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import {
  MATCH_VARIANT_GUIDE_ROWS,
  NIST_MATCH_1N_GUIDE_ROWS,
  CONTEXT_FACE_MATCH,
  buildAcceptAttribute,
  formatListLabel,
  ISO_RECORD_LABEL,
  isPlainImageForContext,
  isIsoRecordInput,
  resolveInputKind,
  API_VARIANT_RAW,
  API_VARIANT_NIST,
  API_VARIANT_MINEX,
  MODALITY_FACE,
  MINEX_FACE_IRIS_BLOCKED_MESSAGE,
  normalizeApiVariant,
} from './biosdkRoutes';
import { useBiosdkRoute } from './hooks/useBiosdkRoute';
import {
  matchNistFaceWithGalleryFiles,
  matchNistFaceMxNMatrix,
  pickBestGalleryMatch,
  rankGalleryDecisions,
} from './nistFaceMatch';
import { isNistPlainImageExtension } from './nistGalleryFile';
import MultiFileUpload from './components/MultiFileUpload';
import NistOneToNResults, { NistMxNMatrixResults } from './components/NistOneToNResults';
import { bdbToUint8Array, isBchqTemplate } from './bdbUtils';
import RoutePreviewChip from './components/RoutePreviewChip';
import InputTypeBadge from './components/InputTypeBadge';
import VariantStatusBanner from './components/VariantStatusBanner';
import RequestSummaryCard from './components/RequestSummaryCard';
import VariantTestingGuide from './components/VariantTestingGuide';
import VariantBlockedBanner from './components/VariantBlockedBanner';
import { User, Upload, Loader2, AlertCircle, Clock, ArrowLeftRight } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Label } from './components/ui/label';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { resolveIsMatch, parseOptionalNumber } from './matchResultUtils';

function MatcherInput({ apiVariant = 'mosip' }) {
  const isNistMode = normalizeApiVariant(apiVariant) === API_VARIANT_NIST;
  const isMinexMode = normalizeApiVariant(apiVariant) === API_VARIANT_MINEX;
  const [sampleFile, setSampleFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [error, setError] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [requestSummary, setRequestSummary] = useState(null);
  const [sampleImageUrl, setSampleImageUrl] = useState(null);
  const [galleryImageUrl, setGalleryImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convert1: 0, convert2: 0, convertSample: 0, convertGallery: 0, match: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [galleryExtracted, setGalleryExtracted] = useState(isNistMode);
  const [nistGalleryFiles, setNistGalleryFiles] = useState([]);
  const [nistOneToN, setNistOneToN] = useState(false);
  const [nistTable6MxN, setNistTable6MxN] = useState(false);
  const [nistTopK, setNistTopK] = useState(5);
  const [nistGalleryResults, setNistGalleryResults] = useState([]);
  const [nistMxNMatrix, setNistMxNMatrix] = useState([]);
  const [jp2isoSampleImageUrl, setjp2IsoSampleImageUrl] = useState(null);
  const [jp2isoGalleryImageUrl, setjp2IsoGalleryImageUrl] = useState(null);

  const fileExtensions = useMemo(() => {
    const exts = [];
    if (sampleFile) exts.push(sampleFile.name.split('.').pop().toLowerCase());
    if (galleryFile) exts.push(galleryFile.name.split('.').pop().toLowerCase());
    return exts;
  }, [sampleFile, galleryFile]);

  useEffect(() => {
    if (isNistMode) setGalleryExtracted(true);
  }, [isNistMode]);

  const { route, variantStatus, inputTypeLabel, makeRequestSummary } = useBiosdkRoute(
    BIOSDK_API_BASE_URL,
    isNistMode ? 'match' : 'process',
    fileExtensions,
    apiVariant,
    CONTEXT_FACE_MATCH,
    isNistMode || isMinexMode ? MODALITY_FACE : undefined
  );

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const resetState = () => {
    setMatchResult(null);
    setRequestSummary(null);
    setShowAdvanced(false);
    setError(null);
    setProcessing(false);
  };

  const convertFaceImageToIso = async (inputFile) => {
    const formData = new FormData();
    formData.append('file', inputFile);
    return axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FACE`, formData, {
      responseType: 'arraybuffer',
    });
  };

  const isNistProbePlainImage = (ext) =>
    isPlainImageForContext(ext, CONTEXT_FACE_MATCH) || (isNistMode && isNistPlainImageExtension(ext));

  const handleSampleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setSampleFile(selectedFile);
    resetState();

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let convertSampleDuration1 = 0;

    try {
      if (isNistProbePlainImage(fileExtension)) {
        setSampleImageUrl(URL.createObjectURL(selectedFile));
      } else if (isNistMode && isIsoRecordInput(fileExtension, CONTEXT_FACE_MATCH)) {
        setSampleImageUrl(null);
        setjp2IsoSampleImageUrl(null);
        setSampleFile(selectedFile);
        setTiming((prevTiming) => ({
          ...prevTiming,
          convert1: convertSampleDuration1,
        }));
        return;
      } else if (isIsoRecordInput(fileExtension, CONTEXT_FACE_MATCH)) {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setSampleImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoSampleImageUrl(null);
      } else if (resolveInputKind(fileExtension, CONTEXT_FACE_MATCH) === 'unsupported') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp or an ISO record.');
        setSampleFile(null);
        setSampleImageUrl(null);
        return;
      } else {
        setError('Unsupported file type.');
        setSampleFile(null);
        setSampleImageUrl(null);
        return;
      }
      setTiming((prevTiming) => ({
        ...prevTiming,
        convert1: convertSampleDuration1,
      }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setSampleFile(null);
      setSampleImageUrl(null);
    }
  };

  const handleGalleryFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setGalleryFile(selectedFile);
    resetState();

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let convertGalleryDuration1 = 0;

    try {
      if (galleryExtracted && !isIsoRecordInput(fileExtension, CONTEXT_FACE_MATCH)) {
        setError('When gallery is marked as extracted, upload only an ISO record for gallery.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
      if (isPlainImageForContext(fileExtension, CONTEXT_FACE_MATCH)) {
        setGalleryImageUrl(URL.createObjectURL(selectedFile));
      } else if (isIsoRecordInput(fileExtension, CONTEXT_FACE_MATCH)) {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setGalleryImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoGalleryImageUrl(null);
      } else if (resolveInputKind(fileExtension, CONTEXT_FACE_MATCH) === 'unsupported') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp or an ISO record.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      } else {
        setError('Unsupported file type.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
      setTiming((prevTiming) => ({
        ...prevTiming,
        convert2: convertGalleryDuration1,
      }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setGalleryFile(null);
      setGalleryImageUrl(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasGallery =
      isNistMode && (nistOneToN || nistTable6MxN) ? nistGalleryFiles.length > 0 : Boolean(galleryFile);
    if (!sampleFile || !hasGallery) {
      setError('Please upload both sample and gallery images.');
      return;
    }

    if (variantStatus?.isRawBlocked || variantStatus?.isNistBlocked || variantStatus?.isMinexBlocked) {
      setError(variantStatus.blockedReason);
      return;
    }

    setProcessing(true);

    if (isNistMode) {
      try {
        const startConvertSample = performance.now();
        const galleryTargets = nistOneToN || nistTable6MxN ? nistGalleryFiles : [galleryFile];

        if (nistTable6MxN) {
          const { matrix } = await matchNistFaceMxNMatrix(
            BIOSDK_API_BASE_URL,
            sampleFile,
            galleryTargets
          );
          setNistMxNMatrix(matrix);
          setNistGalleryResults([]);
          const flatBest = matrix.flatMap((row) => row.decisions);
          const best = pickBestGalleryMatch(flatBest) || flatBest[0];
          setMatchResult({
            score: best?.score,
            threshold: best?.threshold,
            isMatch: resolveIsMatch(best?.score, best?.threshold, best?.decision?.match, 'similarity'),
            scoreLabel: 'Similarity (best M×N cell)',
            galleryIndex: best?.galleryIndex,
          });
          setRequestSummary(
            makeRequestSummary(
              'match',
              `${sampleFile.name}, ${galleryTargets.map((f) => f.name).join(', ')}`,
              {
                'Match type': 'NIST Table 6 M×N',
                'Probe templates': String(matrix.length),
                'Gallery candidates': String(galleryTargets.length),
              }
            )
          );
          setTiming({
            convert1: timing.convert1,
            convert2: timing.convert2,
            convertSample: performance.now() - startConvertSample,
            convertGallery: 0,
            match: 0,
          });
          setShowAdvanced(true);
          setError(null);
          setProcessing(false);
          return;
        }

        const { matchResponse, decisions } = await matchNistFaceWithGalleryFiles(
          BIOSDK_API_BASE_URL,
          sampleFile,
          galleryTargets,
          'single'
        );
        const convertSampleDuration = performance.now() - startConvertSample;
        setNistMxNMatrix([]);

        const statusCode = matchResponse?.data?.response?.statusCode;
        if (statusCode && statusCode !== 200) {
          throw new Error(matchResponse?.data?.response?.statusMessage || 'Face match failed');
        }

        const ranked = rankGalleryDecisions(decisions, 'FACE');
        setNistGalleryResults(ranked);
        const best = pickBestGalleryMatch(ranked) || ranked[0];
        const faceDecision = best?.decision || {};
        const analytics = faceDecision.analyticsInfo || best?.analytics || {};
        const score = best?.score ?? parseOptionalNumber(faceDecision.score ?? analytics.similarity);
        const threshold =
          best?.threshold ?? parseOptionalNumber(faceDecision.threshold ?? analytics.threshold);
        const isMatch = resolveIsMatch(score, threshold, faceDecision.match, 'similarity');

        setMatchResult({
          score,
          threshold,
          isMatch,
          scoreLabel: 'Similarity',
          galleryIndex: best?.galleryIndex,
        });
        setRequestSummary(
          makeRequestSummary(
            'match',
            `${sampleFile.name}, ${galleryTargets.map((f) => f.name).join(', ')}`,
            {
              'Match type': nistOneToN ? 'NIST 1:N BCHQ' : 'NIST BCHQ template',
              'Gallery candidates': String(galleryTargets.length),
            }
          )
        );
        setError(null);
        setTiming({
          convert1: timing.convert1,
          convert2: timing.convert2,
          convertSample: convertSampleDuration,
          convertGallery: 0,
          match: Number(analytics.matchDuration) || 0,
        });
        setShowAdvanced(true);
      } catch (err) {
        setError(`Error: ${err.response?.data?.message || err.message || 'An error occurred'}`);
        setMatchResult(null);
      } finally {
        setProcessing(false);
      }
      return;
    }

    let f1 = [];
    let f2 = [];
    let convertSampleDuration = 0;
    let convertGalleryDuration = 0;
    try {
      const startConvertSample = performance.now();
      const sampleFileExtension = sampleFile.name.split('.').pop().toLowerCase();
      
      if (galleryExtracted) {
        if (isPlainImageForContext(sampleFileExtension, CONTEXT_FACE_MATCH)) {
          const isoResp = await convertFaceImageToIso(sampleFile);
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(isoResp.data);
        } else if (isIsoRecordInput(sampleFileExtension, CONTEXT_FACE_MATCH)) {
          const sampleIsoArrayBuffer = await sampleFile.arrayBuffer();
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(sampleIsoArrayBuffer);
        } else {
          throw new Error('Unsupported sample file type for extracted gallery. Use plain images or an ISO record.');
        }
      } else if (
        isPlainImageForContext(sampleFileExtension, CONTEXT_FACE_MATCH) ||
        isIsoRecordInput(sampleFileExtension, CONTEXT_FACE_MATCH)
      ) {
        const sampleIsoArrayBuffer = await sampleFile.arrayBuffer();
        const endConvertSample = performance.now();
        convertSampleDuration = endConvertSample - startConvertSample;
        f1 = toSignedByteJson(sampleIsoArrayBuffer);
      } else {
        throw new Error('Unsupported sample file type. Use plain images or an ISO record.');
      }

      const startConvertGallery = performance.now();
      const galleryFileExtension = galleryFile.name.split('.').pop().toLowerCase();
      if (galleryExtracted && !isIsoRecordInput(galleryFileExtension, CONTEXT_FACE_MATCH)) {
        throw new Error('When gallery is marked as extracted, upload only an ISO record for gallery.');
      }
      if (
        isPlainImageForContext(galleryFileExtension, CONTEXT_FACE_MATCH) ||
        isIsoRecordInput(galleryFileExtension, CONTEXT_FACE_MATCH)
      ) {
        const GalleryisoArrayBuffer = await galleryFile.arrayBuffer();
        const endConvertGallery = performance.now();
        convertGalleryDuration = endConvertGallery - startConvertGallery;
        f2 = toSignedByteJson(GalleryisoArrayBuffer);
      } else {
        throw new Error('Unsupported gallery file type. Use plain images or an ISO record.');
      }

      const templateResponse = await fetch('/face_match_inner_request-template.json');
      if (!templateResponse.ok) {
        throw new Error(`Template fetch failed with status: ${templateResponse.status}`);
      }

      let templateText = await templateResponse.text();
      templateText = templateText
        .replace('sample_bdb', f1)
        .replace('gallery_bdb', f2);

      const versionRequestTemplateResponse = await fetch('/match_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedTemplate = btoa(templateText);
      const finalRequestBody = JSON.parse(
        versionRequestTemplateText.replace('encoded_request', base64EncodedTemplate)
      );

      finalRequestBody.operation = 'match';
      const matchUrl = route.url;
      logBiosdkUiRoute('FaceMatcher', matchUrl.includes('/raw/') ? 'rawRoute' : 'isoRoute', matchUrl, {
        operation: 'process',
      });
      const matchResponse = await axios.post(matchUrl, finalRequestBody, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      const statusCode = matchResponse?.data?.response?.statusCode;
      if (statusCode && statusCode !== 200) {
        throw new Error(matchResponse?.data?.response?.statusMessage || 'Face match failed');
      }

      const faceDecision =
        matchResponse?.data?.response?.response?.[0]?.decisions?.FACE ||
        matchResponse?.data?.response?.response?.[0]?.decisions?.[Object.keys(matchResponse?.data?.response?.response?.[0]?.decisions || {})[0]] ||
        {};
      const analytics = faceDecision.analyticsInfo || {};
      const matchDuration = Number(analytics.matchDuration || 0);

      if (matchResponse.data.errors && matchResponse.data.errors.length > 0) {
        setError(`Error: ${matchResponse.data.errors[0].message}`);
        setMatchResult(null);
      } else {
        const decision = faceDecision;
        const score = parseOptionalNumber(
          analytics.score ?? analytics.SCORE ?? decision.score ?? analytics.matchScore
        );
        const threshold = parseOptionalNumber(
          analytics.threshold ?? analytics.THRESHOLD ?? decision.threshold ?? analytics.matchThreshold
        );
        const isMatch = resolveIsMatch(score, threshold, decision.match, 'distance');

        setMatchResult({
          score,
          threshold,
          isMatch,
        });
        setRequestSummary(makeRequestSummary(
          'match',
          `${sampleFile.name}, ${galleryFile.name}`
        ));
        setError(null);
      }

      setTiming({
        convert1: timing.convert1,
        convert2: timing.convert2,
        convertSample: convertSampleDuration,
        convertGallery: convertGalleryDuration,
        match: matchDuration,
      });
      setShowAdvanced(true);

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(`Error: ${errorMessage}`);
      setMatchResult(null);
    } finally {
      setProcessing(false); 
    }
  };

  const handleReset = () => {
    setSampleFile(null);
    setGalleryFile(null);
    setSampleImageUrl(null);
    setGalleryImageUrl(null);
    setGalleryExtracted(isNistMode);
    setNistGalleryFiles([]);
    setNistOneToN(false);
    setNistTable6MxN(false);
    setNistGalleryResults([]);
    setNistMxNMatrix([]);
    setMatchResult(null);
    setRequestSummary(null);
    setError(null);
    setShowAdvanced(false);
  };

  // Face matcher backend returns a 0..1 cosine/euclidean score. The UI shows
  // it as a 0..100 percentage (e.g. 0.89 -> "89.00%"). If the backend ever
  // sends a value already in 0..100 (rare), keep it as-is.
  const toPercent = (value) => {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return null;
    return num <= 1.0 && num >= -1.0 ? num * 100 : num;
  };

  const formatScore = (value) => {
    const pct = toPercent(value);
    if (pct === null) return 'N/A';
    return `${pct.toFixed(2)}%`;
  };

  const getProgressWidth = (score) => {
    const pct = toPercent(score);
    if (pct === null) return 0;
    return Math.max(0, Math.min(100, pct));
  };

  const nistFileAccept = buildAcceptAttribute(API_VARIANT_NIST, CONTEXT_FACE_MATCH);

  const ImageUploadCard = ({ title, file, preview, onFileChange, type, subtitle, acceptOverride, inputId }) => (
    <Card className="flex-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
        {subtitle && <CardDescription className="text-xs">{subtitle}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="upload-zone-small">
          <input
            type="file"
            id={inputId || `file-face-${type}`}
            accept={
              acceptOverride ||
              (type === 'gallery' && galleryExtracted
                ? '.iso,.bin'
                : buildAcceptAttribute(apiVariant, CONTEXT_FACE_MATCH))
            }
            onChange={onFileChange}
            className="hidden"
          />
          <label htmlFor={inputId || `file-face-${type}`} className="cursor-pointer">
            {preview ? (
              <div className="relative">
                <img src={preview} alt={`${title} preview`} className="w-full h-32 object-contain rounded" />
                <Badge variant="face" className="absolute top-1 right-1 text-xs">Face</Badge>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-xs text-gray-600">Click to upload</p>
              </>
            )}
          </label>
        </div>
        {file && <p className="text-xs text-gray-500 text-center truncate">{file.name}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="biochq-page">
      <div className="biochq-container-wide">
        <Card className="biochq-card face">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face Matcher</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              {isNistMode
                ? 'NIST / FRVT face match — BCHQ templates via /raw/match'
                : 'Match via unified /process — MOSIP variant: /mosip/process'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <VariantBlockedBanner
              apiVariant={apiVariant}
              message={isMinexMode ? MINEX_FACE_IRIS_BLOCKED_MESSAGE : null}
            />
            <div className="space-y-6">
              {isNistMode && (
                <div className="max-w-lg mx-auto space-y-2">
                  <div className="flex items-center gap-2 justify-center">
                    <input
                      type="checkbox"
                      id="nistOneToN"
                      checked={nistOneToN}
                      disabled={nistTable6MxN}
                      onChange={(e) => {
                        if (nistTable6MxN && !e.target.checked) return;
                        setNistOneToN(e.target.checked);
                        if (e.target.checked) setNistTable6MxN(false);
                        setGalleryFile(null);
                        setNistGalleryFiles([]);
                        setGalleryImageUrl(null);
                        setNistGalleryResults([]);
                        setNistMxNMatrix([]);
                        setMatchResult(null);
                        setError(null);
                      }}
                      className="rounded disabled:opacity-60"
                    />
                    <Label
                      htmlFor="nistOneToN"
                      className={`text-sm ${nistTable6MxN ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      1:N match — probe vs N gallery (images or .bin)
                      {nistTable6MxN && (
                        <span className="block text-xs text-gray-400">(on while Table 6 M×N is enabled)</span>
                      )}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <input
                      type="checkbox"
                      id="nistTable6MxN"
                      checked={nistTable6MxN}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setNistTable6MxN(enabled);
                        if (enabled) {
                          setNistOneToN(true);
                        }
                        setGalleryFile(null);
                        setNistGalleryFiles([]);
                        setGalleryImageUrl(null);
                        setNistGalleryResults([]);
                        setNistMxNMatrix([]);
                        setMatchResult(null);
                        setError(null);
                      }}
                      className="rounded"
                    />
                    <Label htmlFor="nistTable6MxN" className="text-sm text-gray-600">
                      Table 6 M×N — group photo probe vs N gallery
                    </Label>
                  </div>
                </div>
              )}
              {!isNistMode && (
                <div className="max-w-md mx-auto">
                  <div className="flex items-center gap-2 justify-center">
                    <input
                      type="checkbox"
                      id="faceGalleryExtracted"
                      checked={galleryExtracted}
                      onChange={(e) => {
                        setGalleryExtracted(e.target.checked);
                        setGalleryFile(null);
                        setGalleryImageUrl(null);
                        setjp2IsoGalleryImageUrl(null);
                        setMatchResult(null);
                        setError(null);
                      }}
                      className="rounded"
                    />
                    <Label htmlFor="faceGalleryExtracted" className="text-sm text-gray-600">
                      Gallery image is already extracted
                    </Label>
                  </div>
                </div>
              )}
              {/* Image Upload Section */}
              <div className="flex gap-4 items-stretch">
                <ImageUploadCard
                  title={nistTable6MxN ? 'Group photo probe' : 'Sample Face'}
                  subtitle={
                    nistTable6MxN
                      ? 'One group image — multiple faces become probe templates (Table 6)'
                      : isNistMode
                        ? 'Plain image (.jpg, .png, .ppm, …) or BCHQ .bin'
                        : undefined
                  }
                  file={sampleFile}
                  preview={sampleImageUrl}
                  onFileChange={handleSampleFileChange}
                  type="sample"
                  inputId="file-face-nist-probe"
                  acceptOverride={isNistMode ? nistFileAccept : undefined}
                />

                <div className="flex items-center justify-center">
                  <div className="compare-icon">
                    <ArrowLeftRight className="h-6 w-6 text-primary" />
                  </div>
                </div>

                {isNistMode && (nistOneToN || nistTable6MxN) ? (
                  <Card className="flex-1">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">
                        {nistTable6MxN ? 'Gallery (M×N)' : 'Gallery (1:N)'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <MultiFileUpload
                        inputId="file-face-nist-gallery"
                        files={nistGalleryFiles}
                        onChange={(next) => {
                          setNistGalleryFiles(next);
                          setGalleryFile(next[0] || null);
                          setMatchResult(null);
                          setNistGalleryResults([]);
                          setNistMxNMatrix([]);
                          setError(null);
                        }}
                        accept={nistFileAccept}
                        multiple
                        label="Upload gallery images or BCHQ templates"
                        hint="Plain images auto-extract to BCHQ; ranked by similarity (descending)"
                        addMoreLabel="Add more gallery files"
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <ImageUploadCard
                    title="Gallery Face"
                    file={galleryFile}
                    preview={galleryImageUrl}
                    onFileChange={handleGalleryFileChange}
                    type="gallery"
                  />
                )}
              </div>

              {(sampleFile || galleryFile || nistGalleryFiles.length > 0) && (
                <div className="max-w-xl mx-auto space-y-3">
                  <InputTypeBadge fileExtensions={fileExtensions} apiVariant={apiVariant} contextKey={CONTEXT_FACE_MATCH} />
                  <VariantStatusBanner
                    variantStatus={
                      sampleFile &&
                      (galleryFile || (isNistMode && (nistOneToN || nistTable6MxN) && nistGalleryFiles.length))
                        ? variantStatus
                        : null
                    }
                  />
                  {sampleFile &&
                    (galleryFile || (isNistMode && (nistOneToN || nistTable6MxN) && nistGalleryFiles.length)) &&
                    route.url && <RoutePreviewChip url={route.url} />}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !sampleFile ||
                    (isNistMode && (nistOneToN || nistTable6MxN) ? !nistGalleryFiles.length : !galleryFile) ||
                    processing ||
                    variantStatus?.isNistBlocked ||
                    variantStatus?.isMinexBlocked
                  }
                  variant="face"
                  size="lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <User className="mr-2 h-4 w-4" />
                      Match Faces
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} variant="outline" size="lg" disabled={processing}>
                  Reset
                </Button>
              </div>

              {/* Error Display */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {requestSummary && <RequestSummaryCard summary={requestSummary} />}

              {/* Match Result */}
              {matchResult && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Score Visualization */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="relative pt-4">
                          <div className="flex mb-2 items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600">0%</span>
                            <span className="text-xs font-semibold text-gray-600">100%</span>
                          </div>
                          <div className="overflow-hidden h-4 text-xs flex rounded-full bg-gray-200">
                            <div
                              style={{ width: `${getProgressWidth(matchResult.score)}%` }}
                              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                                matchResult.isMatch ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                            />
                          </div>
                          <p className="text-center mt-3 text-2xl font-bold text-primary">
                            {formatScore(matchResult.score)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Match Details */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score:</span>
                          <span className="font-medium">{formatScore(matchResult.score)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Threshold:</span>
                          <span className="font-medium">{formatScore(matchResult.threshold)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Match Status:</span>
                          <Badge variant={matchResult.isMatch ? 'success' : 'warning'}>
                            {matchResult.isMatch ? 'MATCHED' : 'NOT MATCHED'}
                          </Badge>
                        </div>
                        {matchResult.galleryIndex != null && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Best gallery index:</span>
                            <span className="font-medium">{matchResult.galleryIndex}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {nistMxNMatrix.length > 0 && (
                    <NistMxNMatrixResults
                      matrix={nistMxNMatrix}
                      galleryFileNames={nistGalleryFiles.map((f) => f.name)}
                      modality="FACE"
                    />
                  )}

                  {nistGalleryResults.length > 1 && !nistMxNMatrix.length && (
                    <NistOneToNResults
                      decisions={nistGalleryResults}
                      modality="FACE"
                      topK={nistTopK}
                      onTopKChange={setNistTopK}
                      galleryFileNames={nistGalleryFiles.map((f) => f.name)}
                    />
                  )}

                  {/* Timing Details */}
                  {showAdvanced && (
                    <Card className="timing-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Timing Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-sm text-center">
                          <div>
                            <p className="text-gray-600">Sample Convert</p>
                            <p className="font-medium">{timing.convertSample.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Gallery Convert</p>
                            <p className="font-medium">{timing.convertGallery.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Match Time</p>
                            <p className="font-medium text-primary">{timing.match} ms</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              <VariantTestingGuide title="Variant testing guide (Match)" rows={MATCH_VARIANT_GUIDE_ROWS} />
              {isNistMode && (
                <VariantTestingGuide title="NIST 1:N match testing" rows={NIST_MATCH_1N_GUIDE_ROWS} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MatcherInput;
